import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { CheckCircle, XCircle, Clock, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { DermScaleRenderer } from '../components/DermScaleRenderer';
import { MasiRenderer } from '../components/MasiRenderer';
import { CustomTableRenderer, customTableMissing } from '../components/CustomTable';
import { CompositeScaleRenderer, compositeScaleMissing } from '../components/CompositeScale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KNOWN_TYPES = ['section_header', 'likert_1_5', 'likert', 'derm_scale', 'masi', 'free_text', 'select', 'radio', 'multiselect', 'checkbox', 'nps', 'scale', 'text', 'date', 'textarea', 'table_text', 'table_select', 'custom_table', 'checklist', 'composite_scale', 'nps_multi', 'boolean', 'boolean_with_obs'];

export default function PublicSurvey() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/survey/${token}`)
      .then(r => setState({ loading: false, data: r.data, error: null }))
      .catch(e => setState({ loading: false, error: e.response?.data?.detail || 'Error', status: e.response?.status }));
  }, [token]);

  const [missingIds, setMissingIds] = useState([]);
  const setAnswer = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
    setMissingIds(prev => prev.filter(x => x !== qid && !qid.startsWith(`${x}::`)));
  };

  const submit = async () => {
    const allItems = state.data?.questionnaire?.questions || [];
    const questions = allItems.filter(q => q.type !== 'section_header');
    const missing = questions.filter(q => {
      const a = answers[q.id];
      // URS-F241: table_text y table_select -> SIEMPRE todas las filas obligatorias
      if (q.type === 'table_text' || q.type === 'table_select') {
        const obj = (typeof a === 'object' && a) ? a : {};
        return (q.options || []).some(opt => !(obj[opt] || '').toString().trim());
      }
      // URS-F352: custom_table -> todas las celdas interiores obligatorias.
      if (q.type === 'custom_table') {
        return customTableMissing(q, a).length > 0;
      }
      // URS-F354: nps_multi -> todos los sub-apartados obligatorios.
      if (q.type === 'nps_multi') {
        const obj = (typeof a === 'object' && a) ? a : {};
        const subs = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
        return subs.some(sub => !(obj[sub] || '').toString().trim());
      }
      // URS-F358: composite_scale -> todos los sub-apartados obligatorios.
      if (q.type === 'composite_scale') {
        return compositeScaleMissing(q, a).length > 0;
      }
      // Satisfacción: todas las preguntas obligatorias.
      if (a === undefined || a === null) return true;
      if (typeof a === 'string') return !a.trim();
      if (Array.isArray(a)) return a.length === 0;
      if (typeof a === 'object') return Object.keys(a).filter(k => k !== '_observations').length === 0;
      return false;
    });
    questions.forEach(q => {
      if (q.type === 'boolean_with_obs' && answers[q.id] === 'si' && !(answers[`${q.id}_obs`] || '').trim() && !missing.includes(q)) missing.push(q);
    });
    // URS-F262: validar follow_up_texts obligatorios (con lógica only_if_low)
    const followUpMissing = [];
    questions.forEach(q => {
      const fus = q.follow_up_texts || [];
      if (fus.length === 0) return;
      const likertVal = answers[q.id];
      const isLow = likertVal === '1' || likertVal === '2';
      fus.forEach(ft => {
        if (!ft.required) return;
        if (ft.only_if_low && !isLow) return;
        const v = answers[`${q.id}::${ft.id}`];
        if (!v || !v.toString().trim()) followUpMissing.push(ft.id);
      });
    });
    if (missing.length > 0 || followUpMissing.length > 0) {
      const total = missing.length + followUpMissing.length;
      const ids = [...missing.map(q => q.id), ...questions.filter(q => (q.follow_up_texts || []).some(ft => followUpMissing.includes(ft.id))).map(q => q.id)];
      setMissingIds(ids);
      toast.error(`Todas las preguntas son obligatorias: faltan ${total} por responder`);
      const el = document.querySelector(`[data-testid="survey-q-${ids[0]}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      const responses = [];
      questions.forEach(q => {
        responses.push({ question_id: q.id, value: answers[q.id] ?? '' });
        if (q.type === 'boolean_with_obs' && answers[q.id] === 'si') responses.push({ question_id: `${q.id}_obs`, value: answers[`${q.id}_obs`] || '' });
        (q.follow_up_texts || []).forEach(ft => {
          const v = answers[`${q.id}::${ft.id}`];
          if (v && v.toString().trim()) {
            responses.push({ question_id: `${q.id}::${ft.id}`, value: v, parent_question_id: q.id, follow_up_id: ft.id });
          }
        });
      });
      await axios.post(`${API_URL}/api/public/survey/${token}/submit`, { responses });
      setState(prev => ({ ...prev, data: { ...prev.data, status: 'completed' } }));
    } catch (e) {
      toast.error(errText(e, 'Error al enviar'));
    }
    setSubmitting(false);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state.error) {
    const isExpired = state.status === 410;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-4">
        <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-8 max-w-md text-center">
          {isExpired ? <Clock size={48} className="text-amber-500 mx-auto mb-3" /> : <XCircle size={48} className="text-rose-500 mx-auto mb-3" />}
          <h1 className="text-xl font-heading text-stone-800 mb-2">{isExpired ? 'Enlace caducado' : 'Enlace no disponible'}</h1>
          <p className="text-sm text-stone-500">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;

  if (d.status === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-4">
        <div className="bg-white border border-emerald-200 rounded-sm shadow-sm p-10 max-w-md text-center" data-testid="survey-completed">
          <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading text-stone-800 mb-2">¡Gracias!</h1>
          <p className="text-sm text-stone-600">Hemos recibido tu respuesta correctamente.</p>
          {d.completed_at && (
            <p className="text-xs text-stone-400 mt-3">Completado el {new Date(d.completed_at).toLocaleString('es-ES')}</p>
          )}
        </div>
      </div>
    );
  }

  const questions = d.questionnaire?.questions || [];
  const isSatisfaction = d.questionnaire?.subtype === 'likert_satisfaccion';
  const meta = d.questionnaire?.satisfaction_meta || {};
  const preamble = d.questionnaire?.preamble_es;

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6 mb-4">
          <div className="flex items-center gap-3 mb-2">
            <Sparkle size={22} weight="fill" className="text-[#1f3e80]" />
            <h1 className="text-2xl font-heading text-stone-900">Cuestionario de satisfacción</h1>
          </div>
          <div className="text-sm text-stone-600 space-y-1 mt-3">
            <div><span className="text-stone-400">Estudio:</span> <b>{d.study_title}</b></div>
            <div><span className="text-stone-400">Visita:</span> <b>{d.visit_code}{d.visit_name ? ` — ${d.visit_name}` : ''}</b></div>
            <div><span className="text-stone-400">Voluntario:</span> <b>{d.volunteer_name}</b></div>
          </div>
          {isSatisfaction && (meta.area_name || meta.subarea_name || meta.family_name || meta.periodo_uso) && (
            <div className="mt-3 pt-3 border-t border-stone-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {/* URS-F255: mostrar "Tipo de producto" (area funcional) en lugar
                  de "Producto" (familia comercial). El claim no se muestra al
                  voluntario para evitar sesgo de expectativas. */}
              {(meta.area_name || meta.subarea_name || meta.family_name) && (
                <div>
                  <span className="text-stone-400">Tipo de producto:</span>{' '}
                  <b className="text-stone-800">{meta.area_name || meta.subarea_name || meta.family_name}</b>
                </div>
              )}
              {meta.periodo_uso && <div><span className="text-stone-400">Periodo de uso:</span> <b className="text-stone-800">{meta.periodo_uso}</b></div>}
            </div>
          )}
          {preamble && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <p className="text-sm text-stone-700 leading-relaxed bg-violet-50/40 border border-violet-100 rounded-sm px-3 py-2.5" data-testid="survey-preamble">{preamble}</p>
            </div>
          )}
          <p className="text-xs text-stone-400 mt-3 pt-3 border-t border-stone-100">
            Tu opinión nos ayuda a mejorar. Este cuestionario es anónimo de cara a terceros y solo se usará con fines de investigación y mejora de la calidad.
          </p>
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {(() => {
            let qNum = 0;
            return questions.map((q) => {
              // URS-F263: section header - render como separador visual, sin formulario
              if (q.type === 'section_header') {
                return (
                  <div key={q.id} className="pt-3" data-testid={`survey-section-${q.id}`}>
                    <h2 className="text-base sm:text-lg font-heading text-[#1f3e80] border-b-2 border-[#1f3e80]/30 pb-1.5 mb-1">{q.title}</h2>
                  </div>
                );
              }
              qNum += 1;
              return (
            <div key={q.id} className={`bg-white border rounded-sm shadow-sm p-5 ${missingIds.includes(q.id) ? 'border-rose-400 ring-1 ring-rose-300' : 'border-stone-200'}`} data-testid={`survey-q-${q.id}`}>
              <label className="block text-sm font-medium text-stone-800 mb-3">
                <span className="text-stone-400 font-mono mr-2">{qNum}.</span>
                {q.text}
                <span className="text-rose-500 ml-1">*</span>
              </label>
              {missingIds.includes(q.id) && <p className="text-xs text-rose-600 -mt-2 mb-2" data-testid={`survey-q-missing-${q.id}`}>Esta pregunta es obligatoria</p>}
              {(q.type === 'likert_1_5' || q.type === 'likert') && (() => {
                const size = q.scale_size === 7 ? 7 : 5;
                const nums = Array.from({ length: size }, (_, i) => i + 1);
                const leftLabel = size === 7 ? 'Me disgusta mucho' : 'Totalmente en desacuerdo';
                const rightLabel = size === 7 ? 'Me gusta mucho' : 'Totalmente de acuerdo';
                return (
                  <div>
                    <div className={`grid gap-1.5 sm:gap-2 ${size === 7 ? 'grid-cols-7' : 'grid-cols-5'}`}>
                      {nums.map(n => {
                        const sel = answers[q.id] === String(n);
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={`likert-${q.id}-${n}`}
                            onClick={() => setAnswer(q.id, String(n))}
                            className={`flex flex-col items-center justify-center py-3 px-1 rounded-sm border text-sm font-semibold transition ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80] shadow-sm scale-[1.02]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1f3e80]/40'}`}
                          >
                            <span className="text-lg leading-none">{n}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[10px] text-stone-500 px-1 mt-1.5">
                      <span>1 · {leftLabel}</span>
                      <span>{size} · {rightLabel}</span>
                    </div>
                  </div>
                );
              })()}
              {/* URS-F262: preguntas abiertas de seguimiento bajo cada Likert */}
              {(q.type === 'likert_1_5' || q.type === 'likert') && (q.follow_up_texts || []).length > 0 && (() => {
                const likertVal = answers[q.id];
                const isLow = likertVal === '1' || likertVal === '2';
                const visibleFus = (q.follow_up_texts || []).filter(ft => !ft.only_if_low || isLow);
                if (visibleFus.length === 0) return null;
                return (
                  <div className="mt-3 space-y-3 border-t border-stone-100 pt-3" data-testid={`followups-${q.id}`}>
                    {visibleFus.map(ft => {
                      const key = `${q.id}::${ft.id}`;
                      const val = answers[key] || '';
                      const maxChars = ft.max_chars || 500;
                      const required = ft.required && (!ft.only_if_low || isLow);
                      return (
                        <div key={ft.id} data-testid={`followup-${ft.id}`}>
                          <label className="block text-[13px] font-medium text-stone-700 mb-1.5">
                            {ft.text_es || ''}
                            {required && <span className="text-rose-500 ml-1">*</span>}
                            {ft.only_if_low && (
                              <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                Mostrada por valoración baja
                              </span>
                            )}
                          </label>
                          <Textarea
                            data-testid={`followup-input-${ft.id}`}
                            value={val}
                            maxLength={maxChars}
                            onChange={e => setAnswer(key, e.target.value)}
                            placeholder="Escribe aquí tu respuesta (opcional)"
                            className="bg-white border-stone-200 min-h-[72px] text-sm"
                          />
                          <div className="text-[10px] text-stone-400 text-right mt-0.5">{val.length}/{maxChars}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {q.type === 'derm_scale' && q.scale_id && (
                <DermScaleRenderer
                  scaleId={q.scale_id}
                  value={answers[q.id] || ''}
                  onChange={(v) => setAnswer(q.id, v)}
                  publicMode={true}
                  testIdPrefix={`derm-${q.id}`}
                />
              )}
              {q.type === 'masi' && (
                <MasiRenderer
                  value={answers[q.id] || ''}
                  onChange={(v) => setAnswer(q.id, v)}
                  testIdPrefix={`masi-${q.id}`}
                />
              )}
              {q.type === 'free_text' && (() => {
                const max = q.max_chars || 1000;
                const val = answers[q.id] || '';
                return (
                  <div>
                    <Textarea
                      data-testid={`freetext-${q.id}`}
                      value={val}
                      maxLength={max}
                      onChange={e => setAnswer(q.id, e.target.value)}
                      placeholder="Escribe aquí tu respuesta"
                      className="bg-white border-stone-200 min-h-[100px] text-sm"
                    />
                    <div className="text-[10px] text-stone-400 text-right mt-0.5">{val.length}/{max}</div>
                  </div>
                );
              })()}
              {/* URS-F392: soporte de tipos 'select' (Selector) y 'multiselect'
                  (Multiselección) en el cuestionario publico. Faltaban ambos
                  en el render — la pregunta se mostraba sin opciones. */}
              {(q.type === 'select' || q.type === 'radio') && (
                <select
                  data-testid={`select-${q.id}`}
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  className="w-full h-10 px-3 border border-stone-200 rounded-sm bg-white text-sm focus:outline-none focus:border-[#1f3e80]"
                >
                  <option value="">— Selecciona una opción —</option>
                  {(q.options || []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
              {q.type === 'multiselect' && (
                <div className="space-y-2" data-testid={`multiselect-${q.id}`}>
                  {(q.options || []).map(opt => {
                    const sel = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                    const checked = sel.includes(opt);
                    return (
                      <label key={opt} className="flex items-center gap-3 px-3 py-2 rounded-sm border border-stone-200 hover:bg-stone-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setAnswer(q.id, checked ? sel.filter(x => x !== opt) : [...sel, opt])}
                          className="accent-[#1f3e80]"
                        />
                        <span className="text-sm text-stone-700">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {q.type === 'radio' && (
                <div className="space-y-2">
                  {(q.options || []).map(opt => (
                    <label key={opt} className="flex items-center gap-3 px-3 py-2 rounded-sm border border-stone-200 hover:bg-stone-50 cursor-pointer">
                      <input
                        type="radio"
                        name={q.id}
                        value={opt}
                        checked={answers[q.id] === opt}
                        onChange={() => setAnswer(q.id, opt)}
                        className="accent-[#1f3e80]"
                      />
                      <span className="text-sm text-stone-700">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === 'checkbox' && (
                <div className="space-y-2">
                  {(q.options || []).map(opt => {
                    const sel = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                    const checked = sel.includes(opt);
                    return (
                      <label key={opt} className="flex items-center gap-3 px-3 py-2 rounded-sm border border-stone-200 hover:bg-stone-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setAnswer(q.id, checked ? sel.filter(x => x !== opt) : [...sel, opt])}
                          className="accent-[#1f3e80]"
                        />
                        <span className="text-sm text-stone-700">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {(q.type === 'boolean' || q.type === 'boolean_with_obs') && (
                <div className="space-y-2" data-testid={`survey-bool-${q.id}`}>
                  <div className="flex gap-2">
                    {(q.type === 'boolean' ? ['si', 'no'] : ['si', 'no', 'na']).map(opt => (
                      <button key={opt} type="button" onClick={() => setAnswer(q.id, opt)} data-testid={`survey-bool-${q.id}-${opt}`}
                        className={`px-4 py-2 text-sm font-medium rounded-sm border ${answers[q.id] === opt ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:border-[#1f3e80]'}`}>
                        {opt === 'si' ? 'Sí' : opt === 'no' ? 'No' : 'No aplica'}
                      </button>
                    ))}
                  </div>
                  {q.type === 'boolean_with_obs' && answers[q.id] === 'si' && (
                    <Textarea value={answers[`${q.id}_obs`] || ''} onChange={e => setAnswer(`${q.id}_obs`, e.target.value)}
                      placeholder="Observaciones (obligatorio)" className="bg-white min-h-[60px]" data-testid={`survey-bool-obs-${q.id}`} />
                  )}
                </div>
              )}
              {!KNOWN_TYPES.includes(q.type) && (
                <Textarea value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)} className="bg-white min-h-[60px]" data-testid={`survey-fallback-${q.id}`} />
              )}
              {q.type === 'nps' && (
                <div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setAnswer(q.id, String(n))}
                        className={`w-10 h-10 rounded-sm border text-sm font-semibold transition ${answers[q.id] === String(n) ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1f3e80]'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-[10px] text-stone-400 px-1">
                    <span>Nada probable</span>
                    <span>Muy probable</span>
                  </div>
                </div>
              )}
              {q.type === 'scale' && (
                <div className="flex gap-1.5">
                  {[1,2,3,4,5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAnswer(q.id, String(n))}
                      className={`w-11 h-11 rounded-sm border text-sm font-semibold ${answers[q.id] === String(n) ? 'bg-amber-400 text-white border-amber-400' : 'bg-white text-stone-700 border-stone-200 hover:border-amber-400'}`}
                    >
                      {n}★
                    </button>
                  ))}
                </div>
              )}
              {q.type === 'text' && (
                <Input
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  className="bg-white border-stone-200"
                />
              )}
              {q.type === 'date' && (
                <Input
                  data-testid={`survey-date-${q.id}`}
                  type="date"
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  className="bg-white border-stone-200 w-48"
                />
              )}
              {q.type === 'textarea' && (
                <Textarea
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                  className="bg-white border-stone-200 min-h-[90px]"
                />
              )}
              {q.type === 'table_text' && (
                <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`table-${q.id}`}>
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="text-left font-semibold text-stone-700 px-3 py-2 w-2/5 border-b border-stone-200">&nbsp;</th>
                        <th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200">Respuesta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(q.options || []).map((opt, oi) => {
                        const cur = (typeof answers[q.id] === 'object' && answers[q.id]) || {};
                        const isNum = q.cell_type === 'number';
                        return (
                          <tr key={opt + oi} className="border-t border-stone-100">
                            <td className="px-3 py-2 text-stone-700 align-middle">{opt}</td>
                            <td className="px-3 py-2">
                              <Input
                                type={isNum ? 'number' : 'text'}
                                inputMode={isNum ? 'decimal' : undefined}
                                value={cur[opt] || ''}
                                onKeyDown={isNum ? (e) => { if (['e', 'E', '+'].includes(e.key)) e.preventDefault(); } : undefined}
                                onChange={e => {
                                  let val = e.target.value;
                                  if (isNum) {
                                    val = val.replace(/[^0-9.,-]/g, '');
                                    val = val.replace(/(?!^)-/g, '');
                                    const parts = val.replace(',', '.').split('.');
                                    if (parts.length > 2) val = `${parts[0]}.${parts.slice(1).join('')}`;
                                  }
                                  setAnswer(q.id, { ...cur, [opt]: val });
                                }}
                                className="bg-white h-8 text-sm"
                                data-testid={`table-${q.id}-${oi}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {q.show_observations !== false && (
                    <div className="border-t border-stone-200 bg-stone-50/40 px-3 py-2 space-y-1">
                      <label className="text-[11px] font-medium text-stone-600 block">Observaciones</label>
                      <Textarea
                        value={((typeof answers[q.id] === 'object' && answers[q.id]) || {})._observations || ''}
                        onChange={e => setAnswer(q.id, { ...((typeof answers[q.id] === 'object' && answers[q.id]) || {}), _observations: e.target.value })}
                        placeholder="Comentarios adicionales (opcional)..."
                        className="bg-white min-h-[50px] text-sm"
                        data-testid={`table-obs-${q.id}`}
                      />
                    </div>
                  )}
                </div>
              )}
              {q.type === 'table_select' && (
                <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`table-select-${q.id}`}>
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="text-left font-semibold text-stone-700 px-3 py-2 w-2/5 border-b border-stone-200">&nbsp;</th>
                        <th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200">Respuesta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(q.options || []).map((opt, oi) => {
                        const cur = (typeof answers[q.id] === 'object' && answers[q.id]) || {};
                        return (
                          <tr key={opt + oi} className="border-t border-stone-100">
                            <td className="px-3 py-2 text-stone-700 align-middle">{opt}</td>
                            <td className="px-3 py-2">
                              <select
                                value={cur[opt] || ''}
                                onChange={e => setAnswer(q.id, { ...cur, [opt]: e.target.value })}
                                className="bg-white border border-stone-200 rounded-sm h-8 text-sm px-2 w-full"
                                data-testid={`table-select-${q.id}-${oi}`}
                              >
                                <option value="">— Selecciona —</option>
                                {(q.cell_options || []).map(co => (
                                  <option key={co} value={co}>{co}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {q.show_observations !== false && (
                    <div className="border-t border-stone-200 bg-stone-50/40 px-3 py-2 space-y-1">
                      <label className="text-[11px] font-medium text-stone-600 block">Observaciones</label>
                      <Textarea
                        value={((typeof answers[q.id] === 'object' && answers[q.id]) || {})._observations || ''}
                        onChange={e => setAnswer(q.id, { ...((typeof answers[q.id] === 'object' && answers[q.id]) || {}), _observations: e.target.value })}
                        placeholder="Comentarios adicionales (opcional)..."
                        className="bg-white min-h-[50px] text-sm"
                        data-testid={`table-select-obs-${q.id}`}
                      />
                    </div>
                  )}
                </div>
              )}
              {q.type === 'custom_table' && (() => {
                // URS-F352: tabla NxM personalizada (encuestas publicas).
                // El payload se transmite como objeto con claves "FilaHeader|ColHeader".
                const cur = (typeof answers[q.id] === 'object' && answers[q.id]) || {};
                return (
                  <div className="space-y-2" data-testid={`survey-custom-table-${q.id}`}>
                    <CustomTableRenderer
                      question={q}
                      answers={cur}
                      onChange={(next) => setAnswer(q.id, next)}
                      lang="es"
                    />
                    {q.show_observations !== false && (
                      <div className="border border-stone-200 bg-stone-50/40 rounded-sm px-3 py-2 space-y-1">
                        <label className="text-[11px] font-medium text-stone-600 block">Observaciones</label>
                        <Textarea
                          value={cur._observations || ''}
                          onChange={e => setAnswer(q.id, { ...cur, _observations: e.target.value })}
                          placeholder="Comentarios adicionales (opcional)..."
                          className="bg-white min-h-[50px] text-sm"
                          data-testid={`survey-custom-table-obs-${q.id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
              {q.type === 'checklist' && (() => {
                // URS-F355: lista de verificación pública.
                const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                const items = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                return (
                  <div className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`survey-checklist-${q.id}`}>
                    {items.map((it, ii) => {
                      const checked = cur.includes(it);
                      return (
                        <label
                          key={it + ii}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50 ${ii > 0 ? 'border-t border-stone-100' : ''} ${checked ? 'bg-violet-50/40' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setAnswer(q.id, checked ? cur.filter(x => x !== it) : [...cur, it])}
                            className="w-4 h-4 rounded-sm border-stone-300 text-[#1f3e80] focus:ring-[#1f3e80] cursor-pointer"
                            data-testid={`survey-checklist-cb-${q.id}-${ii}`}
                          />
                          <span className="text-sm text-stone-700 select-none">{it}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
              {q.type === 'composite_scale' && (() => {
                // URS-F358: escala compuesta en encuesta publica (publicMode).
                const cur = (typeof answers[q.id] === 'object' && answers[q.id]) || {};
                return (
                  <div data-testid={`survey-composite-scale-${q.id}`}>
                    <CompositeScaleRenderer
                      question={q}
                      answers={cur}
                      onChange={(next) => setAnswer(q.id, next)}
                      publicMode={true}
                      lang="es"
                    />
                  </div>
                );
              })()}
              {q.type === 'nps_multi' && (() => {
                // URS-F354: NPS múltiple en encuesta pública.
                const cur = (typeof answers[q.id] === 'object' && answers[q.id]) || {};
                const subs = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                return (
                  <div className="space-y-2" data-testid={`survey-nps-multi-${q.id}`}>
                    <div className="border border-stone-200 rounded-sm overflow-hidden">
                      {subs.map((sub, si) => (
                        <div key={sub + si} className={`flex flex-col gap-1.5 px-3 py-2.5 ${si > 0 ? 'border-t border-stone-100' : ''}`}>
                          <span className="text-sm text-stone-700">{sub}</span>
                          <div className="flex flex-wrap gap-1.5">
                            {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
                              const sel = cur[sub] === String(n);
                              return (
                                <button
                                  key={n}
                                  type="button"
                                  onClick={() => setAnswer(q.id, { ...cur, [sub]: String(n) })}
                                  className={`w-9 h-9 rounded-sm border text-sm font-semibold ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'}`}
                                  data-testid={`survey-nps-multi-${q.id}-${si}-${n}`}
                                >
                                  {n}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex justify-between text-[10px] text-stone-400 px-1">
                            <span>0 · Nada probable</span>
                            <span>10 · Muy probable</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {q.show_observations !== false && (
                      <div className="border border-stone-200 bg-stone-50/40 rounded-sm px-3 py-2 space-y-1">
                        <label className="text-[11px] font-medium text-stone-600 block">Observaciones</label>
                        <Textarea
                          value={cur._observations || ''}
                          onChange={e => setAnswer(q.id, { ...cur, _observations: e.target.value })}
                          placeholder="Comentarios adicionales (opcional)..."
                          className="bg-white min-h-[50px] text-sm"
                          data-testid={`survey-nps-multi-obs-${q.id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
              );
            });
          })()}
        </div>

        {/* Submit */}
        <div className="mt-6 flex justify-end">
          <Button
            data-testid="survey-submit-btn"
            disabled={submitting}
            onClick={submit}
            className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm px-8 py-5 text-sm"
          >
            {submitting ? 'Enviando...' : 'Enviar respuestas'}
          </Button>
        </div>

        <p className="text-[11px] text-stone-400 text-center mt-6">
          © Laboratorio Goya — Este enlace caduca el {d.expires_at?.slice(0, 10)}
        </p>
      </div>
    </div>
  );
}
